import { useQueryClient } from '@tanstack/react-query';

import { type User, useSignIn, useSignOut, useSignUp, useUser } from '@praetoid/client';

import { router } from '~/lib/router';
import { clearGlobals } from '~/lib/useGlobalFilter';

import { AuthContext } from './useAuth';

const key = 'praetoid:auth:user';

function getStoredUser(): User | undefined {
	const user = localStorage.getItem(key);
	if (!user) return undefined;
	return JSON.parse(user);
}

function setStoredUser(user: User | null) {
	if (user) {
		localStorage.setItem(key, JSON.stringify(user));
	} else {
		localStorage.removeItem(key);
	}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();

	const {
		data: user,
		isLoading,
		refetch
	} = useUser({
		query: {
			placeholderData: getStoredUser(),
			refetchOnWindowFocus: true,
			staleTime: 5 * 60 * 1000
		}
	});

	const signinMutation = useSignIn({
		mutation: {
			onSuccess: async (data) => {
				queryClient.clear();
				await router.invalidate();

				setStoredUser(data);
				await router.navigate({ to: '/home' });
			}
		}
	});

	const signoutMutation = useSignOut({
		mutation: {
			onSuccess: async () => {
				queryClient.clear();
				await router.invalidate();
				clearGlobals();

				setStoredUser(null);
				await router.navigate({ to: '/signin', params: { from: router.state.location.pathname } });
			}
		}
	});

	const signupMutation = useSignUp({
		mutation: {
			onSuccess: async (data) => {
				setStoredUser(data);
				await refetch();

				await router.invalidate();
				await router.navigate({
					to: '/home'
				});
			}
		}
	});

	const isAuthenticated = !!user;

	return (
		<AuthContext.Provider
			value={{ isAuthenticated, isLoading, user, signoutMutation, signinMutation, signupMutation }}
		>
			{children}
		</AuthContext.Provider>
	);
}
